import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import RadarActionManager, { ActionType } from '../../utils/radarActionManager';
import { cleanHtmlForMarkdown } from '../../utils/markdownUtils';
import './ScrollActionController.sass';
import { useTranslation } from 'react-i18next';

interface ScrollActionControllerProps {
    children: React.ReactNode;
    debug?: boolean;
    enableLogs?: boolean;
    setIsCanvasVisible?: Function;
}

// Compteur global des actions déclenchées
let globalActionCounter = 0;

/**
 * Composant qui détecte les actions dans le contenu et les déclenche lors du défilement
 * Supporte également le rendu du contenu markdown
 */
export const ScrollActionController: React.FC<ScrollActionControllerProps> = ({
    children,
    enableLogs = true,
    setIsCanvasVisible = () => { },
    isVisible = false
}) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [actionElements, setActionElements] = useState<HTMLElement[]>([]);
    const [activeActions, setActiveActions] = useState<Object>({});
    const actionManager = useRef(RadarActionManager.getInstance());
    const actionCountRef = useRef<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const hasInitializedRef = useRef(false);

    // Référence pour éviter les recréations de fonctions à chaque rendu
    const logRef = useRef((message: string, data?: any) => {
        if (enableLogs) {
            console.log(message, data);
        }
    });

    const log = logRef.current;

    const getReverseAction = useCallback((actionType: string): string => {
        if (actionType.startsWith('create')) {
            return actionType.replace('create', 'delete');
        }

        switch (actionType) {
            case 'startCanvas':
                return 'endCanvas';
            case 'endCanvas':
                return 'startCanvas';
            case 'hideScreen':
                return 'showScreen';
            case 'showScreen':
                return 'hideScreen';
            default:
                return `reverse${actionType.charAt(0).toUpperCase()}${actionType.slice(1)}`;
        }
    }, []);

    // Cache pour les textes déjà traités
    const processMarkdownCache = useRef(new Map<string, string>());
    const processChildrenCache = useRef(new Map<any, React.ReactNode>());
    const handleActionTextCache = useRef(new Map<string, React.ReactNode>());

    // Mise en place du scroll listener via une fonction globale
    useEffect(() => {
        return () => {
            console.log("Scroll handler removed");
        };
    }, []);

    // Initialisation des éléments d'action
    useEffect(() => {
        if (!containerRef.current) return;

        actionManager.current.resetState();
        actionCountRef.current = 0;
        setIsLoading(true);

        const setupActionDetection = () => {
            const elements = Array.from(containerRef.current?.querySelectorAll('[data-action]') || []);
            return elements;
        };

        // Exécuter la détection immédiatement
        const elements = setupActionDetection();
        setActionElements(elements as HTMLElement[]);
        setActiveActions({});

        // Désactiver le loader après un court délai
        const loadingTimeout = setTimeout(() => {
            setIsLoading(false);

            // Vérifier à nouveau les actions après le rendu complet
            setTimeout(() => {
                const newElements = Array.from(containerRef.current?.querySelectorAll('[data-action]') || []);
                if (newElements.length > 0 && elements.length === 0) {
                    setActionElements(newElements as HTMLElement[]);
                }
            }, 100);
        }, 500);

        return () => {
            clearTimeout(loadingTimeout);
        };
    }, []);

    // Nettoyer les caches et réinitialiser le flag lors des changements de propriétés
    useEffect(() => {
        processMarkdownCache.current.clear();
        processChildrenCache.current.clear();
        handleActionTextCache.current.clear();
        hasInitializedRef.current = false;
    }, [children]);

    // Utilitaire pour générer un placeholder unique
    function makeActionPlaceholder(actionType: string, uniqueId: string) {
        return `[[ACTION_MARKER:${actionType}:${uniqueId}]]`;
    }

    // Utilitaire pour remplacer les placeholders par des <span ... /> React
    function replacePlaceholders(node: any): any {
        if (typeof node === 'string') {
            const parts = node.split(/(\[\[ACTION_MARKER:[^\]]+\]\])/g);
            return parts.map((part, i) => {
                const match = part.match(/^\[\[ACTION_MARKER:([^:]+):([^\]]+)\]\]$/);
                if (match) {
                    const [, actionType, uniqueId] = match;
                    return (
                        <span
                            key={uniqueId}
                            className="action-marker"
                            data-action={actionType}
                            data-action-id={uniqueId}
                            style={{ width: '1px', height: '1px', backgroundColor: 'red', position: 'relative' }}
                        />
                    );
                }
                return part;
            });
        } else if (Array.isArray(node)) {
            return node.map(replacePlaceholders);
        } else if (React.isValidElement(node)) {
            return React.cloneElement(
                node,
                node.props as any,
                replacePlaceholders((node.props as any).children)
            );
        }
        return node;
    }

    // Fonction utilitaire pour parser le texte brut et générer un tableau React (texte ou <span ... />)
    function parseTextWithActions(text: string) {
        const actionPattern = /\[([\w]+(?:\s+[\w]+=(?:"[^"]*"|'[^']*'|\S+))*)\]/g;
        const result: React.ReactNode[] = [];
        let lastIndex = 0;
        let match;
        while ((match = actionPattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
                result.push(text.slice(lastIndex, match.index));
            }
            const actionText = match[1];
            const firstSpace = actionText.indexOf(' ');
            const actionType = firstSpace > -1 ? actionText.substring(0, firstSpace) : actionText;
            const reverseActionType = getReverseAction(actionType);
            const uniqueId = `${actionType}-${++globalActionCounter}`;

            // Extraire les attributs supplémentaires
            const attributes: Record<string, string> = {};

            // Regex plus robuste pour extraire les attributs
            // Capture: attribut="valeur" ou attribut='valeur' ou attribut=valeur
            if (firstSpace > -1) {
                const attributesText = actionText.substring(firstSpace + 1);
                const attrRegex = /(\w+)=(?:"([^"]*)"|'([^']*)'|(\S+))/g;
                let attrMatch;

                while ((attrMatch = attrRegex.exec(attributesText)) !== null) {
                    const [, attrName, quotedValue1, quotedValue2, unquotedValue] = attrMatch;
                    const value = quotedValue1 || quotedValue2 || unquotedValue;
                    attributes[`data-${attrName}`] = value;
                }
            }

            result.push(
                <span
                    key={uniqueId}
                    className="action-marker"
                    data-action={actionType}
                    data-reverse-action={reverseActionType}
                    data-action-id={uniqueId}
                    style={{ width: '1px', height: '1px', backgroundColor: 'red', position: 'relative' }}
                    {...attributes}
                />
            );
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
            result.push(text.slice(lastIndex));
        }
        return result;
    }

    // Analyser les enfants pour trouver les codes d'action dans le texte
    const processChildren = useCallback((children: React.ReactNode): React.ReactNode => {
        const cacheKey = children;

        if (processChildrenCache.current.has(cacheKey)) {
            return processChildrenCache.current.get(cacheKey);
        }

        let result;

        if (typeof children === 'string') {
            // Découpe le texte en morceaux React (texte ou <span ... />)
            const parsed = parseTextWithActions(children);
            // Passe chaque morceau à ReactMarkdown séparément, ou compose le rendu toi-même
            result = parsed.map((part, i) =>
                typeof part === 'string'
                    ? <ReactMarkdown key={i}>{part}</ReactMarkdown>
                    : part
            );
        } else if (React.isValidElement(children)) {
            if (children.props && children.props.dangerouslySetInnerHTML) {
                const html = children.props.dangerouslySetInnerHTML.__html;
                if (typeof html === 'string') {
                    const markdownContent = cleanHtmlForMarkdown(html);
                    const parsed = parseTextWithActions(markdownContent);
                    result = parsed.map((part, i) =>
                        typeof part === 'string'
                            ? <ReactMarkdown key={i}>{part}</ReactMarkdown>
                            : part
                    );
                } else {
                    result = children;
                }
            } else {
                result = React.cloneElement(
                    children,
                    {},
                    React.Children.map(children.props.children, processChildren)
                );
            }
        } else if (Array.isArray(children)) {
            result = React.Children.map(children, processChildren);
        } else {
            if (enableLogs) log(t('scrollActionController.non_text_content'), children);
            return children;
        }

        processChildrenCache.current.set(cacheKey, result);
        return result;
    }, [enableLogs, t]);

    // Rendre le contenu de l'article
    const renderArticleContent = useCallback((content: React.ReactNode): React.ReactNode => {
        if (!content) return null;

        const cleanProps = (props: any) => {
            const cleanedProps = { ...props };
            if ('node' in cleanedProps) {
                delete cleanedProps.node;
            }
            return cleanedProps;
        };

        return (
            <div>{content}</div>
        );
    }, []);

    const processedChildren = useMemo(() => processChildren(children), [children, processChildren]);
    const articleContent = useMemo(() => {
        return renderArticleContent(processedChildren);
    }, [processedChildren, renderArticleContent]);

    const getActionKey = useCallback((action: HTMLElement) => {
        return action.getAttribute('data-action-id')
    }, []);

    useEffect(() => {
        if (!containerRef.current || actionElements.length === 0) return;

        let lastScrollY = window.scrollY;

        function handleScrollEvent() {

            const currentScrollY = document.querySelector('.scroll-action-container')?.getBoundingClientRect().top || 0;
            const isScrollingDown = currentScrollY > lastScrollY;
            const isStale = currentScrollY == lastScrollY;
            const isProgressing = !isScrollingDown && !isStale;
            const isReverse = isScrollingDown && !isStale;

            if (isStale) {
                return;
            }


            lastScrollY = currentScrollY;

            const actions = (
                isProgressing ? actionElements.filter(a => getActionKey(a) && !activeActions[getActionKey(a)]) :
                    isReverse ? actionElements.filter(a => getActionKey(a) && activeActions[getActionKey(a)])
                        : []
            );

            // Vérifier chaque élément d'action
            (actions).forEach(element => {
                const rect = element.getBoundingClientRect();

                const actionType = isProgressing ? element.getAttribute('data-action') : element.getAttribute('data-reverse-action');
                const actionKey = getActionKey(element);

                const viewportMiddle = window.innerHeight / 2;
                const isAboveViewportMiddle = rect.top < viewportMiddle;
                const isVisible = rect.top < window.innerHeight && rect.top > 0;

                let shouldTriggerAction = ((isAboveViewportMiddle && isProgressing) || (!isAboveViewportMiddle && isReverse)) && isVisible;

                // Déclencher l'action si elle n'est pas déjà active et si l'élément est au-dessus du milieu de la fenêtre
                if (shouldTriggerAction) {
                    console.log(`🚀 DÉCLENCHEMENT: ${actionType}`);

                    // Ajouter l'action à la liste des actions actives
                    if (isProgressing) {
                        activeActions[actionKey] = true;

                    } else {
                        delete activeActions[actionKey];
                    }

                    // Extraire les paramètres
                    const params: Record<string, string> = {};
                    Array.from(element.attributes).forEach(attr => {
                        if (attr.name.startsWith('data-') && attr.name !== 'data-action' && attr.name !== 'data-reverse-action') {
                            const paramName = attr.name.replace('data-', '');
                            params[paramName] = attr.value;
                        }
                    });

                    // Exécuter l'action
                    try {
                        actionManager.current.executeAction({
                            type: actionType as ActionType,
                            params
                        });

                        console.log(`✅ Action ${actionType} exécutée avec succès`);

                        // Gestion spécifique pour les actions qui affectent la visibilité du canvas
                        if (actionType === 'startCanvas') {
                            setIsCanvasVisible(true);
                        } else if (actionType === 'endCanvas') {
                            setIsCanvasVisible(false);
                        }
                    } catch (error) {
                        console.error(`❌ Erreur lors de l'exécution de ${actionType}:`, error);
                    }

                }
            });

            setActiveActions(activeActions);
        }

        // Attacher tous les écouteurs d'événements possibles
        window.addEventListener('scroll', handleScrollEvent, { passive: true, capture: true });
        containerRef.current.addEventListener('scroll', handleScrollEvent, { passive: true, capture: true });

        // Exécuter une fois au chargement pour les éléments déjà visibles
        setTimeout(handleScrollEvent, 10);

        return () => {
            window.removeEventListener('scroll', handleScrollEvent, { passive: true, capture: true });
            if (containerRef.current) {
                containerRef.current.removeEventListener('scroll', handleScrollEvent, { passive: true, capture: true });
            }
        };
    }, [actionElements]);

    // Debugger le contenu reçu
    useEffect(() => {
        if (typeof children === 'object' && React.isValidElement(children)) {
            const element = children as React.ReactElement;
            if (element.props && element.props.dangerouslySetInnerHTML) {
                const html = element.props.dangerouslySetInnerHTML.__html;
                if (typeof html === 'string') {
                    if (html.includes("[")) {
                        const actionsMatch = html.match(/\[([\w]+(?:\s+[\w]+=(?:"[^"]*"|'[^']*'|\S+))*)\]/g);
                    }
                }
            }
        } else if (typeof children === 'string') {
            console.log("Children is a string, sample:", children.substring(0, 200) + "...");
            console.log("Contains action markers:", children.includes("["));
        }
    }, [children]);

    return (
        <div
            ref={containerRef}
            className={`scroll-action-container${isLoading ? ' is-loading' : ''}`}
            style={{ height: '100%' }}
        >
            {isLoading && (
                <div className="scroll-action-loader">
                    <div className="loader-spinner"></div>
                    <p className="loader-text">{t('messages.please_wait')}</p>
                </div>
            )}

            {!isLoading && (
                <>
                    <div className="article-content-wrapper">
                        {articleContent}

                        <div className={`progress-line ${isVisible ? 'visible' : 'hidden'}`}></div>
                        <div className={`progress-line-after ${isVisible ? 'visible' : 'hidden'}`}></div>
                    </div>
                </>
            )}
        </div>
    );
}; 